import type { ReactNode } from "react";
import { Alert, Button, Spinner } from "@codestack/ui";

export function LoadingState({ label = "Loading…" }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-3 rounded-xl border border-white/[0.08] bg-white/[0.02] px-6 py-16 text-sm text-ink-400">
      <Spinner className="h-4 w-4" />
      {label}
    </div>
  );
}

export function ErrorState({
  message,
  onRetry,
}: {
  message: string;
  onRetry?: () => void;
}) {
  return (
    <div className="rounded-xl border border-rose-400/20 bg-rose-400/[0.06] p-6">
      <p className="text-sm font-semibold text-rose-200">Something went wrong</p>
      <p className="mt-1.5 text-sm text-rose-100/70">{message}</p>
      {onRetry ? (
        <Button variant="secondary" size="sm" className="mt-4" onClick={onRetry}>
          Try again
        </Button>
      ) : null}
    </div>
  );
}

export function ForbiddenState({ message }: { message: string }) {
  return (
    <div className="rounded-xl border border-amber-400/20 bg-amber-400/[0.06] p-8 text-center">
      <p className="text-sm font-semibold text-amber-200">Access denied</p>
      <p className="mx-auto mt-2 max-w-md text-sm text-amber-100/70">{message}</p>
      <p className="mt-3 text-xs text-amber-100/50">
        Permissions are enforced by the API. Contact an owner if you need access.
      </p>
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="rounded-xl border border-dashed border-white/10 bg-white/[0.01] px-6 py-14 text-center">
      <p className="text-sm font-semibold text-ink-200">{title}</p>
      {description ? (
        <p className="mx-auto mt-1.5 max-w-sm text-sm text-ink-500">{description}</p>
      ) : null}
      {action ? <div className="mt-5 flex justify-center">{action}</div> : null}
    </div>
  );
}

export function InlineAlert({
  tone = "info",
  children,
}: {
  tone?: "info" | "success" | "warning" | "danger";
  children: ReactNode;
}) {
  return <Alert tone={tone}>{children}</Alert>;
}
